import { FC } from 'react';
import SchedulePopup from 'components/posts/detail/overview/footer/popups/SchedulePopup';
import AssignTasksPopup from 'components/posts/detail/overview/footer/popups/AssignTasksPopup';
import SendForApprovalPopup from 'components/posts/detail/overview/footer/popups/SendForApprovalPopup';
import { PostStatus } from '@kontentino/kontentino-constants/Posts';
import { useUpdateApprovalPostStatus } from 'app/modules/posts/modules/detail/hooks/useUpdateApprovalPostStatus';
import UserpilotUtils from 'app/utils/userpilot';
import { usePostOverviewQuery } from 'app/modules/posts/modules/post/hooks/usePostOverview';
import { PostPopups } from 'app/modules/posts/modules/post/hooks/usePostPopups';
import { useHistoryQueryParams } from 'app/hooks/useHistoryQueryParams';
import PostsConfig from 'app/config/posts';
import { PostCommunicationContent } from 'app/modules/posts/modules/post/components/postCommunication/types/postCommunicationContent';

type Props = {
  active?: PostPopups;
  id: number;
  hidePopup: () => void;
  onApprovalSent?: (type: 'internal' | 'client') => void;
};

const Popups: FC<Props> = ({ active, id, hidePopup, onApprovalSent }) => {
  const { data } = usePostOverviewQuery(id);
  const updateApprovalStatus = useUpdateApprovalPostStatus({
    postId: id,
  });
  const queryParamHistory = useHistoryQueryParams();

  function onUpdated(status: PostStatus) {
    trackSuccessUpdate();
    updateApprovalStatus.updateOptimistically(status);
    hidePopup();
  }

  const trackSuccessUpdate = () => {
    const eventNames: Record<string, string> = {
      [PostPopups.InternalApproval]:
        UserpilotUtils.events.POST_SENT_FOR_INTERNAL_APPROVAL,
      [PostPopups.ClientApproval]:
        UserpilotUtils.events.POST_SENT_FOR_EXTERNAL_APPROVAL,
      [PostPopups.Schedule]: UserpilotUtils.events.POST_SCHEDULED,
    };

    if (active && eventNames[active]) {
      UserpilotUtils.track(eventNames[active]);
    }
  };

  if (data) {
    const commonProps = {
      postId: data.id,
      pageId: data.page.id,
      pageTimezone: data.page.timezone,
      onUpdated,
    };

    return (
      <>
        {active?.split('-')[0] === PostPopups.Schedule && (
          <SchedulePopup
            post={data}
            onUpdated={onUpdated}
            type={active}
            hidePopup={hidePopup}
          />
        )}
        {active === PostPopups.InternalApproval && (
          <SendForApprovalPopup
            {...commonProps}
            onUpdated={(status) => {
              onUpdated(status);
              if (onApprovalSent) {
                onApprovalSent('internal');
              } else {
                queryParamHistory.setQueryParameter(
                  PostsConfig.COMMUNICATION_TAB_QUERY_PARAM_KEY,
                  PostCommunicationContent.INTERNAL,
                );
              }
            }}
            type="internal"
          />
        )}
        {active === PostPopups.ClientApproval && (
          <SendForApprovalPopup
            {...commonProps}
            onUpdated={(status) => {
              onUpdated(status);
              if (onApprovalSent) {
                onApprovalSent('client');
              } else {
                queryParamHistory.setQueryParameter(
                  PostsConfig.COMMUNICATION_TAB_QUERY_PARAM_KEY,
                  PostCommunicationContent.CLIENT,
                );
              }
            }}
            type="client"
          />
        )}
        {active === PostPopups.AssignTask && (
          <AssignTasksPopup {...commonProps} />
        )}
      </>
    );
  }

  return null;
};

export default Popups;
